import { useCallback, useEffect, useMemo } from 'react';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import { useHandleSubmitWrongPolicyNoCDV } from 'forms/hooks';
import { filter, includes, isEmpty, prop, sortBy } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';

import useFormFieldValue from '@gef-ui/form-core/hooks/state/useFormFieldValue';
import Autocomplete from '@gef-ui/form-material/Autocomplete';
import SubmitButton from '@gef-ui/form-material/SubmitButton';
import TextField from '@gef-ui/form-material/TextField';
import { getLabelMessage } from '@gef-ui/localization/messages';

import { ComponentLoader } from 'components';
import Agency from 'components/atoms/GefMui/OrganizationStructure/Agency';
import Channel from 'components/atoms/GefMui/OrganizationStructure/Channel';
import Manager from 'components/atoms/GefMui/OrganizationStructure/Manager';
import Ppz from 'components/atoms/GefMui/OrganizationStructure/Ppz';
import Region from 'components/atoms/GefMui/OrganizationStructure/Region';
import Seller from 'components/atoms/GefMui/OrganizationStructure/Seller';
import { campaignStates } from 'constants/campaigns';
import { codelists } from 'constants/codelists';
import { MaxLengths } from 'constants/forms';
import { AUTO_FILL_RULES, USER_ROLES } from 'constants/general';
import campaigns from 'features/views/campaigns';
import { load, reload } from 'features/views/campaigns/actions';
import { getRunningCampaigns } from 'features/views/campaigns/selectors';
import contactList from 'features/views/contactList';
import { bruttoProdStatusValues } from 'features/views/contactList/constants';
import { changeContactState } from 'features/views/contacts/actions';
import { CONTACTS_FORM_NAME, FIELDS } from 'features/views/contacts/form';
import user from 'features/views/user';
import { getCodelistByName } from 'utils/codelists';

const fetchStorePath = `${campaigns.constants.url}.contacts`;

export const ContactsForm = () => {
	const dispatch = useDispatch();

	const campaigns = useSelector((state) => getRunningCampaigns(state, 'contacts'));
	const currentRole = useSelector(user.selectors.getCurrentRole);
	const bruttoProductionStatus = useFormFieldValue({ fieldName: FIELDS.bruttoProductionStatus });
	const solutionStates = useSelector(getCodelistByName(codelists.solutionStateVirtual));

	useEffect(() => {
		dispatch(
			load('contacts')({
				role: currentRole,
				onlyWithContacts: true,
				campaignStateId: [campaignStates.RUNNING],
				size: 1000,
				page: 0,
			}),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch]);

	const campaignOptions = useMemo(() => sortBy(prop('fullName'))(campaigns), [campaigns]);
	const contactStatesByUserRole = useMemo(
		() =>
			filter((item) => {
				if (includes(currentRole, [USER_ROLES.PPZ, USER_ROLES.SELLER])) {
					return item.sellerPpz;
				} else return true;
			})(solutionStates),
		[currentRole, solutionStates],
	);

	const handleBruttoChange = useCallback(() => {
		dispatch(changeContactState());
	}, [dispatch]);

	const handleSubmit = useCallback(() => {
		dispatch(contactList.actions.submitForm());
	}, [dispatch]);

	const handleSubmitWrongPolicyNoCDV = useHandleSubmitWrongPolicyNoCDV({ continueFn: handleSubmit });

	const handleResetContactList = useCallback(() => {
		dispatch(contactList.actions.resetContactList());
	}, [dispatch]);

	const handleReloadCampaigns = useCallback(() => {
		dispatch(reload('contacts'));
	}, [dispatch]);

	// agreement with BE
	// codelist made by FE - there is no need to track separete codelist in DB for these values
	const bruttoProductionStatusCodelist = useMemo(
		() => [
			{
				code: bruttoProdStatusValues.PAIRED,
				name: getLabelMessage('contacts:paired'),
			},
			{
				code: bruttoProdStatusValues.NOT_PAIRED,
				name: getLabelMessage('contacts:notPaired'),
			},
		],
		[],
	);

	return (
		<Grid container>
			<Grid size={12}>
				<Grid container spacing={2}>
					<Grid size={12}>
						<ComponentLoader fetchStorePath={fetchStorePath} onReload={handleReloadCampaigns}>
							<Autocomplete
								size="small"
								fieldName={FIELDS.campaign}
								label={getLabelMessage('contacts:campaign')}
								options={campaignOptions}
								labelPropName="fullName"
								valuePropName="id"
								disabled={isEmpty(campaigns)}
							/>
						</ComponentLoader>
					</Grid>
					<Grid size={4}>
						<Channel
							fieldName={FIELDS.channel}
							formName={CONTACTS_FORM_NAME}
							disableOnOneOptions={!includes(currentRole, AUTO_FILL_RULES.CENTRAL)}
							autoSelect
							isGuarantor={currentRole === USER_ROLES.GUARANTOR}
						/>
					</Grid>
					<Grid size={4}>
						<Region
							fieldName={FIELDS.region}
							formName={CONTACTS_FORM_NAME}
							disableOnOneOptions={!includes(currentRole, AUTO_FILL_RULES.CENTRAL)}
							autoSelect
							isGuarantor={currentRole === USER_ROLES.GUARANTOR}
						/>
					</Grid>
					<Grid size={4}>
						<Agency
							fieldName={FIELDS.agency}
							formName={CONTACTS_FORM_NAME}
							disableOnOneOptions={!includes(currentRole, AUTO_FILL_RULES.REGION_MANAGER)}
							autoSelect
						/>
					</Grid>
					<Grid size={4}>
						<Manager
							fieldName={FIELDS.manager}
							formName={CONTACTS_FORM_NAME}
							disableOnOneOptions={!includes(currentRole, AUTO_FILL_RULES.AGENCY_MANAGER)}
							autoSelect
						/>
					</Grid>
					<Grid size={4}>
						<Seller
							fieldName={FIELDS.seller}
							formName={CONTACTS_FORM_NAME}
							disableOnOneOptions={!includes(currentRole, AUTO_FILL_RULES.SELLER)}
							autoSelect
						/>
					</Grid>
					<Grid size={4}>
						<Ppz fieldName={FIELDS.ppz} formName={CONTACTS_FORM_NAME} disableOnOneOptions autoSelect />
					</Grid>
					<Tooltip title={getLabelMessage('tooltips:policyNo')}>
						<Grid size={2}>
							<TextField
								size="small"
								fieldName={FIELDS.policyNumber}
								label={getLabelMessage('contacts:policyNo')}
								slotProps={{
									htmlInput: {
										maxLength: MaxLengths.POLICY_NO,
									},
								}}
							/>
						</Grid>
					</Tooltip>
					<Grid size={3}>
						<TextField
							size="small"
							fieldName={FIELDS.product}
							label={getLabelMessage('contacts:product')}
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.PRODUCT,
								},
							}}
						/>
					</Grid>
					<Grid size={2}>
						<TextField
							size="small"
							fieldName={FIELDS.firstName}
							label={getLabelMessage('contacts:name')}
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.NAME,
								},
							}}
						/>
					</Grid>
					<Grid size={2}>
						<TextField
							size="small"
							fieldName={FIELDS.surname}
							label={getLabelMessage('contacts:surname')}
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.SURNAME,
								},
							}}
						/>
					</Grid>
					<Grid size={3}>
						<TextField
							size="small"
							fieldName={FIELDS.businessName}
							label={getLabelMessage('contacts:businessName')}
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.BUSINESS_NAME,
								},
							}}
						/>
					</Grid>
					<Tooltip title={getLabelMessage('tooltips:birthNumber')}>
						<Grid size={2}>
							<TextField
								size="small"
								fieldName={FIELDS.personIn}
								label={getLabelMessage('contacts:personIn')}
								slotProps={{
									htmlInput: {
										maxLength: MaxLengths.PERSON_IN,
									},
								}}
							/>
						</Grid>
					</Tooltip>
					<Tooltip title={getLabelMessage('tooltips:taxNumber')}>
						<Grid size={2}>
							<TextField
								size="small"
								fieldName={FIELDS.businessIn}
								label={getLabelMessage('contacts:businessIn')}
								slotProps={{
									htmlInput: {
										maxLength: MaxLengths.TAX_NUMBER,
									},
								}}
							/>
						</Grid>
					</Tooltip>
					<Grid size={2}>
						<TextField
							size="small"
							fieldName={FIELDS.municipality}
							label={getLabelMessage('contacts:municipality')}
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.MUNICIPALITY,
								},
							}}
						/>
					</Grid>
					<Tooltip title={getLabelMessage('tooltips:postalCode')}>
						<Grid size={1}>
							<TextField
								size="small"
								fieldName={FIELDS.zipCode}
								label={getLabelMessage('contacts:zipCode')}
								slotProps={{
									htmlInput: {
										maxLength: MaxLengths.ZIP_CODE,
									},
								}}
							/>
						</Grid>
					</Tooltip>
					<Grid size={3}>
						<Autocomplete
							size="small"
							fieldName={FIELDS.contactState}
							label={getLabelMessage('contacts:solutionState')}
							options={contactStatesByUserRole}
							labelPropName="shortDescription"
							disabled={includes(bruttoProductionStatus, [
								bruttoProdStatusValues.PAIRED,
								bruttoProdStatusValues.NOT_PAIRED,
							])}
						/>
					</Grid>
					<Grid size={2}>
						<Autocomplete
							size="small"
							fieldName={FIELDS.bruttoProductionStatus}
							label={getLabelMessage('contacts:production')}
							options={bruttoProductionStatusCodelist}
							labelPropName="name"
							valuePropName="code"
							onChange={handleBruttoChange}
						/>
					</Grid>
					<Grid size={6} />
					<Grid size={3}>
						<Button variant="outlined" onClick={handleResetContactList} fullWidth>
							{getLabelMessage('common:resetButton')}
						</Button>
					</Grid>
					<Grid size={3}>
						<SubmitButton
							variant="contained"
							onClick={handleSubmit}
							onClickError={handleSubmitWrongPolicyNoCDV}
							fullWidth
							text={getLabelMessage('contacts:search')}
						/>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};

export default ContactsForm;
